import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface AssignPlanData {
  userId: string;
  planId: string;
  duration: string;
}

export interface AssignedPlan {
  id: string;
  user: {
    id: string;
    firstname: string;
    lastname: string;
    email: string;
  };
  plan: {
    id: string;
    planName: string;
    price: number;
    duration: number;
  };
  duration: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssignPlanApiResponse {
  success: boolean;
  message: string;
  data?: {
    assignPlan: AssignedPlan;
  };
}

export interface AssignPlansApiResponse {
  success: boolean;
  message: string;
  data?: {
    assignPlans: AssignedPlan[];
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// CREATE ASSIGNED PLAN
export const createAssignedPlan = createAsyncThunk<
  AssignPlanApiResponse,
  AssignPlanData,
  { rejectValue: string }
>("assignPlan/createAssignedPlan", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<AssignPlanApiResponse>(
      "/assign-plan",
      {
        userId: formData.userId,
        planId: formData.planId,
        duration: formData.duration,
      }
    );

    if (response.data.success) {
      toast.success(response.data.message || "Plan assigned successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to assign plan");
      return rejectWithValue(response.data.message || "Failed to assign plan");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as AssignPlanApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET ALL ASSIGNED PLANS
export const getAllAssignedPlans = createAsyncThunk<
  AssignPlansApiResponse,
  void,
  { rejectValue: string }
>("assignPlan/getAllAssignedPlans", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<AssignPlansApiResponse>(
      "/assign-plan"
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve assigned plans");
      return rejectWithValue(
        response.data.message || "Failed to retrieve assigned plans"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as AssignPlansApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET ASSIGNED PLAN BY ID
export const getAssignedPlanById = createAsyncThunk<
  AssignPlanApiResponse,
  string,
  { rejectValue: string }
>("assignPlan/getAssignedPlanById", async (id, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<AssignPlanApiResponse>(
      `/assign-plan/${id}`
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve assigned plan");
      return rejectWithValue(
        response.data.message || "Failed to retrieve assigned plan"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as AssignPlanApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// UPDATE ASSIGNED PLAN
export const updateAssignedPlan = createAsyncThunk<
  AssignPlanApiResponse,
  { id: string; data: Partial<AssignPlanData> },
  { rejectValue: string }
>(
  "assignPlan/updateAssignedPlan",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.put<AssignPlanApiResponse>(
        `/assign-plan/${id}`,
        data
      );

      if (response.data.success) {
        toast.success(response.data.message || "Plan updated successfully");
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to update plan");
        return rejectWithValue(
          response.data.message || "Failed to update plan"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as AssignPlanApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// DELETE ASSIGNED PLAN
export const deleteAssignedPlan = createAsyncThunk<
  AssignPlanApiResponse,
  string,
  { rejectValue: string }
>("assignPlan/deleteAssignedPlan", async (id, { rejectWithValue }) => {
  try {
    const response = await createApiClient.delete<AssignPlanApiResponse>(
      `/assign-plan/${id}`
    );

    if (response.data.success) {
      toast.success(response.data.message || "Plan deleted successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to delete plan");
      return rejectWithValue(response.data.message || "Failed to delete plan");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as AssignPlanApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});
